/**
 * ReviewerView — eDMS Web
 *
 * Source: drafts/31_ux_design_system_spec.md §4.3 Diff Comparison Pattern
 *       + drafts/28_wireframe_gap_matrix.md #4
 *
 * Layout (desktop):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ Header: doc number + version + reviewer assignment   │
 *   ├──────────────────────────────────────────────────────┤
 *   │ AI Redline Summary badge (advisory; clearly marked)  │
 *   ├─────────────────────────┬────────────────────────────┤
 *   │ Predecessor (left)      │ New draft (right)          │
 *   │ Side-by-side diff       │ with paragraph-anchored    │
 *   │                         │ comments                   │
 *   ├─────────────────────────┴────────────────────────────┤
 *   │ Comment thread panel (collapsible)                   │
 *   ├──────────────────────────────────────────────────────┤
 *   │ 3-action button row: Approve / Request Changes / Reject │
 *   └──────────────────────────────────────────────────────┘
 *
 * Mobile: stacks vertically; AI badge first, then unified diff, then comments, then actions.
 *
 * Data: workflow detail loaded via TanStack Query; signature challenge requested
 * server-side at click time so the attestation text + version come from the server
 * (per I18N-003).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  StatusBadge,
  SignatureConfirmationModal,
} from '@edms/design-system';
import styles from './ReviewerView.module.css';
import { useCurrentUser, useRequestSignatureChallenge, useWorkflowDetail, useWorkflowDecision, useWorkflowWithdraw } from '../../api/hooks';
import { TrainingImpactWizard } from './TrainingImpactWizard';

interface ReviewerViewProps {
  /** Workflow ID to review. Defaults to a demo workflow so the screen can
   *  be rendered standalone without a router. */
  workflowId?: string;
}

export function ReviewerView({ workflowId = 'wf-abc-123' }: ReviewerViewProps) {
  const { t } = useTranslation();
  const workflowQ = useWorkflowDetail(workflowId);
  const userQ = useCurrentUser();
  const challengeMutation = useRequestSignatureChallenge();
  const decisionMutation = useWorkflowDecision(workflowId);
  const withdrawMutation = useWorkflowWithdraw(workflowId);
  const [showSignModal, setShowSignModal] = useState(false);
  // Inline reason capture for the negative decisions + withdrawal (DMS-22).
  const [pending, setPending] = useState<'REJECTED' | 'CHANGES_REQUESTED' | 'WITHDRAWN' | null>(null);
  const [reason, setReason] = useState('');
  const [signError, setSignError] = useState<string | null>(null);

  if (workflowQ.isLoading) {
    return <p className={styles.empty} role="status">Loading workflow…</p>;
  }
  if (workflowQ.isError || !workflowQ.data) {
    return (
      <p className={styles.empty} role="alert">
        Failed to load workflow {workflowId}. {workflowQ.error?.message}
      </p>
    );
  }

  const wf = workflowQ.data;
  const challenge = challengeMutation.data;

  // The recorded outcome — sourced from the workflow query (keyed per id), so it
  // never leaks across workflows when the screen is reused on a param change.
  const decided = wf.decision ?? null;

  function onApprove() {
    challengeMutation.mutate(
      {
        target_type: 'workflow_step',
        target_id: wf.workflow_id,
        signature_meaning: 'REVIEW',
      },
      { onSuccess: () => setShowSignModal(true) },
    );
  }

  function onConfirmIdentity(_initials: string, password?: string) {
    // ESG-02 — apply the signed decision with real sign-time re-authentication:
    // the re-entered password is verified server-side against Keycloak. The
    // decision is recorded ONLY if the password is valid (Part-11 identity).
    decisionMutation.mutate(
      { decision: 'APPROVED', reauthPassword: password },
      {
        onSuccess: () => { setSignError(null); setShowSignModal(false); },
        onError: (e: unknown) => {
          setShowSignModal(false);
          setSignError(e instanceof Error ? e.message : 'Signature failed — re-authentication did not succeed.');
        },
      },
    );
  }

  function confirmNegative() {
    if (!pending || pending === 'WITHDRAWN') return;
    decisionMutation.mutate(
      { decision: pending, comment: reason.trim() || undefined },
      { onSuccess: () => { setPending(null); setReason(''); } },
    );
  }

  // DMS-22 — terminate the workflow with a justification (audit trail). The
  // signature record stays immutable; this withdraws the in-flight workflow.
  function confirmWithdraw() {
    withdrawMutation.mutate(
      { reason: reason.trim() },
      { onSuccess: () => { setPending(null); setReason(''); } },
    );
  }

  // v0.12.10 — stage progression timeline (item #12). Standard SOP
  // workflow: Draft → Author Review → QA Review → QA Approval →
  // Effective. The current stage is inferred from my_role / IN_REVIEW.
  const stages = [
    { key: 'DRAFT',               label: 'Draft',         done: true,  current: false },
    { key: 'AUTHOR_REVIEW',       label: 'Author review', done: true,  current: false },
    { key: 'IN_REVIEW',           label: 'QA review',     done: false, current: true  },
    { key: 'PENDING_QA_APPROVAL', label: 'QA approval',   done: false, current: false },
    { key: 'EFFECTIVE',           label: 'Effective',     done: false, current: false },
  ];

  return (
    <div className={styles.reviewer}>
      <header className={styles.header}>
        <div>
          <div className={styles.docMeta}>
            <span className={styles.docNumber}>{wf.doc_number}</span>
            <StatusBadge state="IN_REVIEW" />
          </div>
          <h1 className={styles.title}>{wf.title}</h1>
          <p className={styles.subtitle}>
            {wf.version} — Reviewing as <strong>{wf.my_role}</strong>
          </p>
        </div>
      </header>

      {/* Stage progression strip — v0.12.10 polish */}
      <nav className={styles.stageStrip} aria-label="Workflow stage progression">
        {stages.map((s, i) => (
          <span
            key={s.key}
            className={[
              styles.stage,
              s.done && styles.stage_done,
              s.current && styles.stage_current,
            ].filter(Boolean).join(' ')}
          >
            <span className={styles.stageDot} aria-hidden>
              {s.done ? '✓' : s.current ? '●' : i + 1}
            </span>
            <span className={styles.stageLabel}>{s.label}</span>
            {i < stages.length - 1 && <span className={styles.stageRail} aria-hidden />}
          </span>
        ))}
      </nav>

      {/* AI redline summary — clearly badged AI */}
      {wf.ai_redline.enabled && (
        <Card variant="outlined" className={styles.aiCard}>
          <CardHeader>
            <div className={styles.aiHeader}>
              <span className={styles.aiBadge} aria-label="AI advisory">
                ✨ AI ADVISORY
              </span>
              <span className={styles.aiConfidence}>
                Confidence {Math.round(wf.ai_redline.confidence * 100)}%
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <p className={styles.aiSummary}>{wf.ai_redline.summary}</p>
            <p className={styles.aiDisclaimer}>
              {t('reviewer.aiDisclaimer', {
                defaultValue:
                  'This summary is AI-generated and advisory only. Verify against the diff before relying on it. Per Annex 22, the AI cannot trigger approval — you must independently form an opinion.',
              })}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Change rationale */}
      <Card>
        <CardHeader>
          <h2 className={styles.sectionTitle}>
            {t('reviewer.changeRationale', { defaultValue: 'Change Rationale' })}
          </h2>
        </CardHeader>
        <CardBody>
          <p>{wf.change_rationale}</p>
        </CardBody>
      </Card>

      {/* Side-by-side diff */}
      <Card>
        <CardHeader>
          <h2 className={styles.sectionTitle}>
            {t('reviewer.diff', { defaultValue: 'Changes vs Previous Version' })}
          </h2>
        </CardHeader>
        <CardBody>
          <div className={styles.diff} role="region" aria-label="Document diff">
            <div className={styles.diffPanel}>
              <header className={styles.diffPanelHeader}>{wf.previous_version}</header>
              <ol className={styles.paragraphList}>
                {wf.paragraphs.map((p) => (
                  <li key={p.id} className={`${styles.paragraph} ${styles[`p-${p.state}`]}`}>
                    {p.text}
                  </li>
                ))}
              </ol>
            </div>
            <div className={styles.diffPanel}>
              <header className={styles.diffPanelHeader}>{wf.version} (new)</header>
              <ol className={styles.paragraphList}>
                {wf.paragraphs.map((p) => (
                  <li key={p.id} className={`${styles.paragraph} ${styles[`p-${p.state}`]}`}>
                    {p.text}
                    {p.state === 'modified' && (
                      <Button variant="tertiary" size="sm" className={styles.commentBtn}>
                        💬 Comment
                      </Button>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Outcome banner — shown once a decision is recorded. */}
      {decided && (
        <div
          className={`${styles.outcome} ${
            decided.decision === 'APPROVED' ? styles.outcomeApproved
              : decided.decision === 'REJECTED' ? styles.outcomeRejected : styles.outcomeChanges
          }`}
          role="status"
        >
          <strong>
            {decided.decision === 'APPROVED' && t('reviewer.outcomeApproved', { defaultValue: '✓ Signed & approved — advanced to QA approval.' })}
            {decided.decision === 'REJECTED' && t('reviewer.outcomeRejected', { defaultValue: '✕ Rejected — returned to the author.' })}
            {decided.decision === 'CHANGES_REQUESTED' && t('reviewer.outcomeChanges', { defaultValue: '↩ Changes requested — sent back to the author.' })}
          </strong>
          {decided.reason && <span className={styles.outcomeReason}>“{decided.reason}”</span>}
          {'decided_by' in decided && decided.decided_by && (
            <span className={styles.outcomeMeta}>{decided.decided_by} · {new Date(decided.decided_at).toLocaleString()}</span>
          )}
        </div>
      )}

      {/* DMS-21 — structured training-impact assessment (replaces the bare warning). */}
      {!decided && <TrainingImpactWizard />}

      {/* Inline reason capture for reject / request-changes. */}
      {!decided && pending && (
        <Card variant="outlined" className={styles.reasonCard}>
          <CardBody>
            <label className={styles.reasonLabel} htmlFor="rv-reason">
              {pending === 'REJECTED'
                ? t('reviewer.rejectReason', { defaultValue: 'Reason for rejection (sent to the author)' })
                : pending === 'WITHDRAWN'
                  ? t('reviewer.withdrawReason', { defaultValue: 'Reason for withdrawal (≥ 10 chars; recorded in the audit trail)' })
                  : t('reviewer.changesReason', { defaultValue: 'What changes are required?' })}
            </label>
            <textarea
              id="rv-reason"
              className={styles.reasonInput}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <div className={styles.reasonActions}>
              <Button variant="secondary" onClick={() => { setPending(null); setReason(''); }} disabled={decisionMutation.isPending || withdrawMutation.isPending}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant={pending === 'CHANGES_REQUESTED' ? 'primary' : 'destructive'}
                onClick={pending === 'WITHDRAWN' ? confirmWithdraw : confirmNegative}
                isLoading={pending === 'WITHDRAWN' ? withdrawMutation.isPending : decisionMutation.isPending}
                disabled={pending === 'WITHDRAWN' && reason.trim().length < 10}
              >
                {pending === 'REJECTED'
                  ? t('reviewer.confirmReject', { defaultValue: 'Confirm rejection' })
                  : pending === 'WITHDRAWN'
                    ? t('reviewer.confirmWithdraw', { defaultValue: 'Confirm withdrawal' })
                    : t('reviewer.confirmChanges', { defaultValue: 'Send back for changes' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Actions */}
      {!decided && !pending && (
        <footer className={styles.actions} role="region" aria-label="Reviewer actions">
          <Button variant="destructive" size="lg" onClick={() => setPending('REJECTED')}>
            {t('reviewer.reject', { defaultValue: 'Reject' })}
          </Button>
          <Button variant="secondary" size="lg" onClick={() => setPending('CHANGES_REQUESTED')}>
            {t('reviewer.requestChanges', { defaultValue: 'Request Changes' })}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={onApprove}
            isLoading={challengeMutation.isPending}
          >
            {t('reviewer.approve', { defaultValue: 'Sign and Approve' })}
          </Button>
          <Button variant="tertiary" size="lg" onClick={() => setPending('WITHDRAWN')}>
            {t('reviewer.withdraw', { defaultValue: 'Withdraw' })}
          </Button>
        </footer>
      )}

      {signError && (
        <div className={styles.lockBanner} role="alert">{signError}</div>
      )}

      {/* Signature confirmation modal — only opens after challenge has been issued
          so the attestation text comes from the server (per I18N-003). */}
      {challenge && (
        <SignatureConfirmationModal
          isOpen={showSignModal}
          signerFullName={userQ.data?.full_name ?? ''}
          signerJobTitle={userQ.data?.role ?? ''}
          expectedInitials={userQ.data?.initials ?? ''}
          // REVIEW confirms via session identity (no initials typing), but ESG-02
          // now requires a real password re-authentication, verified server-side
          // against Keycloak — turning this into a genuine Part-11 signature.
          requireInitials={false}
          requirePassword
          signatureMeaning="REVIEW"
          target={`Document ${wf.doc_number} ${wf.version}`}
          utcTimestamp={challenge.challenge_expires_at}
          attestationText={challenge.attestation_text}
          attestationTextVersion={challenge.attestation_version}
          onCancel={() => setShowSignModal(false)}
          onConfirmIdentity={onConfirmIdentity}
        />
      )}
    </div>
  );
}
